import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { deleteMealRemote, loadMealRemote } from "../lib/storage";
import { friendlyError } from "../lib/errors";
import {
  MEAL_TYPE_ICONS,
  MEAL_TYPE_LABELS,
  type LoggedMeal
} from "../lib/types";

export default function MealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meal, setMeal] = useState<LoggedMeal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    loadMealRemote(id)
      .then(setMeal)
      .catch((e) => setError(friendlyError(e)))
      .finally(() => setLoading(false));
  }, [id]);

  async function onDelete() {
    if (!id || !meal) return;
    try {
      await deleteMealRemote(id);
      navigate(-1);
    } catch (e) {
      setError(friendlyError(e));
    }
  }

  if (loading) return <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>;
  if (error) {
    return (
      <div className="p-4 space-y-3">
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200">{error}</div>
        <button onClick={() => navigate(-1)} className="w-full py-3 rounded-lg bg-slate-800 text-slate-100">
          Back
        </button>
      </div>
    );
  }
  if (!meal) return <div className="p-8 text-center text-slate-500 text-sm">Meal not found.</div>;

  const { estimate } = meal;
  const at = new Date(meal.logged_at);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-400">← Back</button>
        <span className="text-sm text-slate-400">Meal details</span>
      </div>

      {meal.photo_data_url ? (
        <img
          src={meal.photo_data_url}
          alt=""
          className="w-full rounded-2xl object-cover bg-slate-800 max-h-80"
        />
      ) : (
        <div className="w-full aspect-square rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center text-7xl">
          {MEAL_TYPE_ICONS[meal.meal_type]}
        </div>
      )}

      <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">{MEAL_TYPE_ICONS[meal.meal_type]}</span>
          <span className="font-semibold">{MEAL_TYPE_LABELS[meal.meal_type]}</span>
          <span className="text-xs text-slate-500 ml-auto">
            {at.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </span>
        </div>
        {meal.caption && (
          <div className="text-sm text-slate-400 italic mb-3">"{meal.caption}"</div>
        )}
        <div className="flex items-end justify-between mb-3">
          <span className="text-4xl font-bold text-slate-100 tabular-nums">
            {Math.round(estimate.totals.calories)}
          </span>
          <span className="text-sm text-slate-400 mb-1">kcal</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <MacroPill label="Protein" value={estimate.totals.protein_g} color="bg-sky-400" />
          <MacroPill label="Carbs" value={estimate.totals.carbs_g} color="bg-amber-400" />
          <MacroPill label="Fat" value={estimate.totals.fat_g} color="bg-violet-400" />
        </div>
      </section>

      {estimate.items.length > 0 && (
        <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800">
          <div className="text-xs text-slate-400 uppercase tracking-wide mb-3">Items</div>
          <ul className="divide-y divide-slate-800/60">
            {estimate.items.map((item, i) => (
              <li key={i} className="py-2.5">
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-slate-100 font-medium">{item.name}</span>
                  <span className="text-sm text-slate-200 tabular-nums">{Math.round(item.calories)}</span>
                </div>
                <div className="flex justify-between items-baseline mt-0.5">
                  <span className="text-xs text-slate-500">{item.portion}</span>
                  <span className="text-[11px]">
                    <span className="text-sky-400">P {item.protein_g.toFixed(0)}</span>
                    <span className="text-amber-400"> · C {item.carbs_g.toFixed(0)}</span>
                    <span className="text-violet-400"> · F {item.fat_g.toFixed(0)}</span>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(meal.source_providers.length > 0 || estimate.notes) && (
        <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-2 text-xs text-slate-500">
          {meal.source_providers.length > 0 && (
            <div>Source: {meal.source_providers.join(", ")}</div>
          )}
          {estimate.notes && <div className="italic">{estimate.notes}</div>}
          {meal.disagreement_pct ? <div>Vision provider disagreement: {meal.disagreement_pct.toFixed(0)}%</div> : null}
        </section>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => navigate(`/edit/${meal.id}`)}
          className="flex-1 py-3 rounded-lg bg-slate-800 text-slate-100"
        >
          Edit
        </button>
        <button
          onClick={onDelete}
          className="flex-1 py-3 rounded-lg border border-rose-800 text-rose-300"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function MacroPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="p-2 rounded bg-slate-800 text-center">
      <div className={`inline-block w-2 h-2 rounded-full ${color} mr-1`} />
      <span className="text-slate-400">{label}</span>
      <div className="text-slate-100 font-semibold tabular-nums">{value.toFixed(1)}g</div>
    </div>
  );
}
